/**
 * TeamManagementPanel — add / remove teammates for a given location.
 *
 * Self-contained UI: owns the new-teammate input, add/remove loading
 * indicators, and the team-level error message. Parent owns the
 * teammates list and is notified of changes via callbacks so it can
 * also drive other state (e.g. auto-select after add, deselect after
 * remove, close the panel, refresh related data).
 *
 * Used inline inside TransferFundsModal today; D.3 will also wrap it
 * in a stacked TeamManagementModal for the dashboard's Adjust footer.
 *
 * Privacy: the "Add Team Member" input takes a raw locationId typed by
 * the user — never autocompletes from a list of locations. That's the
 * established product rule and applies to both consumers.
 */

import { useState } from 'react';
import { Loader2, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { type Teammate, sortTeammates } from './TeammateSelector';

interface TeamManagementPanelProps {
  /** Caller's location_id (the from_location_id for all add/remove API calls). */
  locationId: string;
  /** Current teammates list (controlled by parent). */
  teammates: Teammate[];
  /**
   * Fires after a successful add. Parent should append the new teammate
   * to its own list and may also auto-select, close the panel, etc.
   */
  onTeammateAdded: (teammate: Teammate) => void;
  /**
   * Fires after a successful remove. Parent should filter the removed
   * teammate from its own list and handle any side effects (e.g.
   * deselect if it was selected).
   */
  onTeammateRemoved: (teammate: Teammate) => void;
  /** Disables all interactions (e.g. while parent is submitting). */
  disabled?: boolean;
}

export function TeamManagementPanel({
  locationId,
  teammates,
  onTeammateAdded,
  onTeammateRemoved,
  disabled = false,
}: TeamManagementPanelProps) {
  const [newTeammateId, setNewTeammateId] = useState('');
  const [addingTeammate, setAddingTeammate] = useState(false);
  const [removingTeammateId, setRemovingTeammateId] = useState<string | null>(null);
  const [teamError, setTeamError] = useState<string | null>(null);

  const handleAddTeammate = async () => {
    if (!newTeammateId.trim()) return;

    setAddingTeammate(true);
    setTeamError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/team-members`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ to_location_id: newTeammateId.trim() }),
        }
      );

      const data = (await response.json()) as {
        success: boolean;
        team_member?: Teammate;
        error?: string;
      };

      if (data.success && data.team_member) {
        setNewTeammateId('');
        onTeammateAdded(data.team_member);
      } else {
        setTeamError(data.error || 'Failed to add teammate');
      }
    } catch {
      setTeamError('Failed to add teammate. Please try again.');
    } finally {
      setAddingTeammate(false);
    }
  };

  const handleRemoveTeammate = async (teammate: Teammate) => {
    setRemovingTeammateId(teammate.id);
    setTeamError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/team-members/${teammate.id}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      const data = (await response.json()) as { success: boolean; error?: string };

      if (data.success) {
        onTeammateRemoved(teammate);
      } else {
        setTeamError(data.error || 'Failed to remove teammate');
      }
    } catch {
      setTeamError('Failed to remove teammate. Please try again.');
    } finally {
      setRemovingTeammateId(null);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      {/* Add Team Member */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Add Team Member
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={newTeammateId}
            onChange={(e) => setNewTeammateId(e.target.value)}
            placeholder="Enter location ID..."
            disabled={addingTeammate || disabled}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <button
            type="button"
            onClick={handleAddTeammate}
            disabled={!newTeammateId.trim() || addingTeammate || disabled}
            className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {addingTeammate ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Find the location ID in your teammate's URL after "/location/"
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Need bulk import? Email support@oasisautokonnect.com with a CSV file.
        </p>
      </div>

      {/* Remove Team Member */}
      {teammates.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Remove Team Member
          </label>
          <div className="space-y-2">
            {sortTeammates(teammates).map((teammate) => (
              <div
                key={teammate.location_id}
                className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md"
              >
                <span className="text-sm text-gray-900">
                  {teammate.display_name || teammate.location_id}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveTeammate(teammate)}
                  disabled={removingTeammateId === teammate.id || disabled}
                  className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                >
                  {removingTeammateId === teammate.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team error message */}
      {teamError && (
        <div className="mt-3 flex items-start p-2 bg-red-50 border border-red-200 rounded-md">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
          <p className="text-sm text-red-700">{teamError}</p>
        </div>
      )}
    </div>
  );
}
